// app/components/Plugins/EventAlerts/EventAlerts.tsx:
'use client'

import React, { useEffect, useState } from 'react'
import { StockEvent } from './StockChart'

/**
 * @uiComponent
 * @name EventAlerts
 * @description The EventAlerts component provides real-time monitoring and alerting for stock-related events.
 * It fetches upcoming events from the API, allows users to set custom alerts, and automatically notifies users
 * when events are approaching. Features include event filtering by ticker symbol and automated polling for timely notifications.
 * @location EventAlerts plugin in the trading terminal
 * @prop {string} ticker - Optional ticker symbol to filter events for a specific stock
 */

/**
 * @uiWorkflow
 * @name Set Up Event Alerts for Stock Events
 * @description How to monitor and get notified about upcoming stock events
 * @platform eventalerts
 * @step 1 Navigate to the Event Alerts plugin [component: EventAlerts] [action: navigate]
 * @step 2 Wait for events to load automatically [component: EventAlerts] [action: wait] [notes: Events fetched from API]
 * @step 3 Review the list of upcoming events [component: EventAlerts] [action: read] [notes: Shows event title and date/time]
 * @step 4 Click "Set Alert" for events you want to monitor [component: EventAlerts] [action: click]
 * @step 5 Confirm alert is activated [component: EventAlerts] [action: observe] [notes: Button changes to "Remove Alert"]
 * @step 6 Repeat for additional events of interest [component: EventAlerts] [action: repeat]
 * @step 7 Keep the application open for notifications [component: EventAlerts] [action: maintain] [notes: Alerts check every 30 seconds]
 * @step 8 Receive browser alert when event approaches [component: EventAlerts] [action: receive] [notes: Notification within 1 minute of event]
 */

/**
 * @uiWorkflow
 * @name Manage Event Alert Subscriptions
 * @description How to add and remove alerts for different stock events
 * @platform eventalerts
 * @step 1 Access the Event Alerts dashboard [component: EventAlerts] [action: navigate]
 * @step 2 Locate events with active alerts [component: EventAlerts] [action: identify] [notes: Shows "Remove Alert" button]
 * @step 3 Click "Remove Alert" to disable notification [component: EventAlerts] [action: click]
 * @step 4 Confirm alert is deactivated [component: EventAlerts] [action: verify] [notes: Button changes to "Set Alert"]
 * @step 5 Browse for new events to monitor [component: EventAlerts] [action: scroll]
 * @step 6 Set alerts for newly discovered events [component: EventAlerts] [action: activate]
 * @step 7 Review your complete alert portfolio [component: EventAlerts] [action: audit]
 */

/**
 * @uiWorkflow
 * @name Filter Events by Stock Symbol
 * @description How to focus on events for specific stocks when ticker is provided
 * @platform eventalerts
 * @step 1 Ensure ticker symbol is passed to component [component: EventAlerts] [action: prerequisite] [notes: Via props or URL parameter]
 * @step 2 Observe filtered event list [component: EventAlerts] [action: view] [notes: Only shows events for specified ticker]
 * @step 3 Review ticker-specific events [component: EventAlerts] [action: analyze] [notes: Earnings, splits, dividends, etc.]
 * @step 4 Set alerts for relevant events [component: EventAlerts] [action: configure]
 * @step 5 Monitor for ticker-specific notifications [component: EventAlerts] [action: track]
 */

interface EventAlertProps {
  ticker?: string // Make ticker optional with '?'
}

const EventAlerts: React.FC<EventAlertProps> = ({ ticker }) => {
  const [events, setEvents] = useState<StockEvent[]>([])
  const [alerts, setAlerts] = useState<string[]>([])

  // Fetch events from the API.
  useEffect(() => {
    // Only add symbol parameter if ticker is provided
    const url = ticker ? `/api/events?symbol=${ticker}` : '/api/events'

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Network response not ok')
        return res.json()
      })
      .then(data => setEvents(data.events))
      .catch(err => console.error('Fetch error:', err))
  }, [ticker]) // Add ticker to dependency array

  // Toggle alert for a given event.
  const toggleAlert = (id: string) => {
    setAlerts(prevAlerts =>
      prevAlerts.includes(id)
        ? prevAlerts.filter(alertId => alertId !== id)
        : [...prevAlerts, id]
    )
  }

  // For demonstration, poll every 30 seconds to see if any alert should fire.
  // (Note: If your events are far in the future, this condition may not be met.)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      events.forEach(event => {
        const eventDate = new Date(event.date)
        if (
          alerts.includes(event.id) &&
          eventDate.getTime() - now.getTime() < 60000 &&
          eventDate.getTime() - now.getTime() > 0
        ) {
          alert(
            `Upcoming event: ${event.title} at ${eventDate.toLocaleString()}`
          )
        }
      })
    }, 30000)
    return () => clearInterval(interval)
  }, [alerts, events])

  return (
    <div>
      <h2>Event Alerts</h2>
      <ul>
        {events.map(event => (
          <li key={event.id}>
            <strong>{event.title}</strong> (
            {new Date(event.date).toLocaleString()}){' '}
            <button onClick={() => toggleAlert(event.id)}>
              {alerts.includes(event.id) ? 'Remove Alert' : 'Set Alert'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default EventAlerts
